import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { Header } from '../components/layout/Header';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';
import { apiClientBff } from '../services/apiBff';
import { API_ENDPOINTS } from '../config/api';

export const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation('forms');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialToken = searchParams.get('token') || '';

  const [token, setToken] = useState(initialToken);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tokenError, setTokenError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const validateToken = () => {
    if (!token.trim()) {
      setTokenError(t('reset_password.token_required'));
      return false;
    }
    setTokenError('');
    return true;
  };

  const validatePassword = () => {
    const value = password;
    if (!value) {
      setPasswordError(t('validation.required'));
      return false;
    }
    if (value.length < 6) {
      setPasswordError(t('validation.password_too_short'));
      return false;
    }
    setPasswordError('');
    return true;
  };

  const validateConfirmPassword = () => {
    if (!confirmPassword) {
      setConfirmPasswordError(t('validation.required'));
      return false;
    }
    if (confirmPassword !== password) {
      setConfirmPasswordError(t('validation.password_mismatch'));
      return false;
    }
    setConfirmPasswordError('');
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const isTokenValid = validateToken();
    const isPasswordValid = validatePassword();
    const isConfirmValid = validateConfirmPassword();

    if (!isTokenValid || !isPasswordValid || !isConfirmValid) {
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      await apiClientBff.authPost(API_ENDPOINTS.AUTH.RESET_PASSWORD, {
        token: token.trim(),
        newPassword: password,
      });

      setSuccessMessage(t('reset_password.success'));
      setTimeout(() => {
        navigate('/auth');
      }, 2000);

    } catch (error: any) {
      const status = error.status || 500;
      if (status === 400 || status === 401 || status === 404 || status === 410) {
        setErrorMessage(t('reset_password.token_invalid_or_expired'));
      } else {
        setErrorMessage(t('reset_password.error_generic'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Header progress={0} />
      <Box
        component="main"
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          pt: '88px',
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            p: { xs: 4, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#ffffff',
            minHeight: { xs: 'auto', md: '100vh' },
          }}
        >
          <Box
            sx={{
              maxWidth: '400px',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ mb: 6 }}>
              <img
                src="/logo.svg"
                alt="MUU Logo"
                style={{ height: 48 }}
              />
            </Box>

            <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
              {t('reset_password.title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3, textAlign: 'center' }}
            >
              {t('reset_password.subtitle')}
            </Typography>

            {successMessage && (
              <Alert severity="success" sx={{ mb: 3, width: '100%' }}>
                {successMessage}
              </Alert>
            )}

            {errorMessage && (
              <Alert severity="error" sx={{ mb: 3, width: '100%' }}>
                {errorMessage}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              <TextField
                fullWidth
                label={t('reset_password.token_label')}
                variant="outlined"
                required
                value={token}
                onChange={(event) => setToken(event.target.value)}
                onBlur={validateToken}
                error={!!tokenError}
                helperText={tokenError}
              />

              <TextField
                fullWidth
                label={t('reset_password.new_password_label')}
                type="password"
                variant="outlined"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                onBlur={validatePassword}
                error={!!passwordError}
                helperText={passwordError}
              />

              <TextField
                fullWidth
                label={t('reset_password.confirm_password_label')}
                type="password"
                variant="outlined"
                required
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                onBlur={validateConfirmPassword}
                error={!!confirmPasswordError}
                helperText={confirmPasswordError}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={isSubmitting}
                sx={{
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#0C5240',
                  boxShadow: '0 4px 12px rgba(12, 82, 64, 0.3)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: '#064E3B',
                    boxShadow: '0 6px 20px rgba(12, 82, 64, 0.4)',
                    transform: 'translateY(-1px)',
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    boxShadow: '0 2px 8px rgba(12, 82, 64, 0.3)',
                  },
                  '&:disabled': {
                    backgroundColor: '#9ca3af',
                    boxShadow: 'none',
                    transform: 'none',
                  },
                }}
              >
                {t('reset_password.submit')}
              </Button>

              <Button
                type="button"
                variant="text"
                fullWidth
                onClick={() => navigate('/auth')}
                sx={{
                  mt: 1,
                  textTransform: 'none',
                  fontWeight: 500,
                  color: DESIGN_TOKENS.colors.primary[700],
                }}
              >
                {t('reset_password.back_to_login')}
              </Button>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            width: { xs: '100%', md: '50%' },
            background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[500]} 0%, ${DESIGN_TOKENS.colors.primary[600]} 50%, ${DESIGN_TOKENS.colors.primary[400]} 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 6,
            minHeight: { xs: '60vh', md: '100vh' },
            borderBottomRightRadius: { xs: 0, md: '24px' },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '24px',
              p: 6,
              maxWidth: '600px',
              width: '100%',
              boxShadow: '0 32px 64px rgba(0, 0, 0, 0.2)',
              position: 'relative',
            }}
            aria-hidden="true"
          >
            <Typography
              variant="body1"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                fontSize: '1.05rem',
                mb: 2,
                fontWeight: 500,
              }}
            >
              {t('reset_password.helper_title')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                fontSize: '0.95rem',
              }}
            >
              {t('reset_password.helper_text')}
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ResetPasswordPage;
